/**
 * What the rhythm side tells the visual side, and the only thing it tells it.
 *
 * This is the whole vocabulary between the two engines. A renderer that wants to
 * react to a hit subscribes to `NOTE_HIT`; it never learns that a `Simulation`
 * exists, what a `Wave` is, or which grade table produced the number. That is
 * what makes the dependency one-directional — and what will let a MIDI keyboard,
 * a replay file or nothing at all drive the same renderers later, because each
 * of those can produce this vocabulary too.
 *
 * Signals come in two kinds and every signal is available as both:
 *
 *   - **Level**: a number you can read on any frame. `AMPLITUDE` is a level.
 *   - **Event**: a thing that happened at an instant. `BEAT` is an event.
 *
 * An event is *also* readable as a level, as a decaying envelope: it jumps to 1
 * when it fires and falls away over its own decay time. That is deliberate. It
 * means a mapping like `BEAT -> rotation` needs no special case — the renderer
 * reads a number for every signal name, and whether that number came from an
 * envelope or a meter is not its business.
 */

export const VISUAL_SIGNALS = [
  'BPM',
  'BEAT',
  'BAR',
  'SECTION',
  'AMPLITUDE',
  'BASS',
  'MID',
  'TREBLE',
  'ONSET',
  'NOTE_HIT',
  'NOTE_MISS',
  'COMBO',
  'SCORE',
  'ACCURACY',
  'TIME',
  'PLAYER_INPUT',
  'CUSTOM_EVENT',
  'CAMERA_EVENT',
  'SONG_EVENT',
] as const;

export type VisualSignalName = (typeof VISUAL_SIGNALS)[number];

/**
 * The signals that are meters rather than moments.
 *
 * Everything not in here is an event, and an event's level is its envelope.
 */
export const LEVEL_SIGNALS: readonly VisualSignalName[] = [
  'BPM',
  'AMPLITUDE',
  'BASS',
  'MID',
  'TREBLE',
  'COMBO',
  'SCORE',
  'ACCURACY',
  'TIME',
];

export function isLevelSignal(name: VisualSignalName): boolean {
  return LEVEL_SIGNALS.includes(name);
}

/**
 * How fast an event's envelope falls back to zero, in seconds.
 *
 * These are not arbitrary. `BEAT` decays inside one beat at a moderate tempo so
 * a renderer driven by it reads as pulsing rather than as permanently lit;
 * `NOTE_MISS` lingers longest because a miss is meant to leave a mark.
 */
export const SIGNAL_DECAY_SECONDS: Readonly<Record<VisualSignalName, number>> = {
  BPM: 0,
  BEAT: 0.35,
  BAR: 0.9,
  SECTION: 2.5,
  AMPLITUDE: 0,
  BASS: 0,
  MID: 0,
  TREBLE: 0,
  ONSET: 0.18,
  NOTE_HIT: 0.45,
  NOTE_MISS: 1.2,
  COMBO: 0,
  SCORE: 0,
  ACCURACY: 0,
  TIME: 0,
  PLAYER_INPUT: 0.25,
  CUSTOM_EVENT: 0.5,
  CAMERA_EVENT: 0.5,
  SONG_EVENT: 0.5,
};

/** Extra detail an event carries. Renderers may ignore all of it. */
export interface VisualSignalEvent {
  readonly name: VisualSignalName;
  /** Song time the event belongs to, from `MusicClock` and nothing else. */
  readonly time: number;
  /** Event strength, 0..1. Drives the envelope; defaults to 1. */
  readonly strength: number;
  /**
   * Everything specific to this occurrence: the grade of a hit, the index of a
   * beat, where on the field a tap landed. Untyped on purpose — the bus must not
   * grow a new field every time a renderer wants to know one more thing.
   */
  readonly detail: Readonly<Record<string, number | string | boolean>>;
}

/**
 * Every signal's level, for one frame.
 *
 * Read-only and re-used: the bus writes into one instance per frame rather than
 * allocating a record per signal per frame, because this is sampled by every
 * layer of every renderer on every frame.
 */
export type VisualSignalFrame = Readonly<Record<VisualSignalName, number>>;

export function emptySignalFrame(): Record<VisualSignalName, number> {
  const frame = {} as Record<VisualSignalName, number>;
  for (const name of VISUAL_SIGNALS) frame[name] = 0;
  return frame;
}

export function isVisualSignalName(value: string): value is VisualSignalName {
  return (VISUAL_SIGNALS as readonly string[]).includes(value);
}
